import * as tokens from '@tokens';
import styled from '@emotion/styled';
import type { ReactNode, ComponentProps, ReactElement } from 'react';
import React from 'react';
import { Icon } from '@components/Icon/Icon';
import type { PropsOf } from '@emotion/react';
import { css } from '@emotion/react';
import { shouldForwardProp } from '@utils/styledProps';
import { TABLE_TH_TOP_BOTTOM_PADDING } from '@tokens/workarounds';
import { color } from '@tokens';

export const DIRECTIONS = ['none', 'asc', 'desc'] as const;

export type ColumnDirection = (typeof DIRECTIONS)[number];

export type HeadCellType = ReactElement<
  ComponentProps<typeof HeadCell>,
  'HeadCell'
>;

export interface HeadCellProps
  extends Omit<
    PropsOf<'th'>,
    'children' | 'abbr' | 'axis' | 'bgColor' | 'headers' | 'as'
  > {
  /**
   * Indicates if the column is sortable or not.
   * Defaults to false
   */
  isSortable?: boolean;

  /**
   * Indicates the current sort direction.
   */
  sortDirection?: ColumnDirection;

  /**
   * Defines the current aria-label value for the sort button.
   */
  sortLabel?: string;

  /**
   * Callback for when the sort direction changes.
   */
  onSort?: (newDirection: ColumnDirection) => void;

  /**
   * The content of the cell.
   */
  children?: string | ReactNode;

  /**
   * Indicates if the cell is in the fixed row or not.
   * For internal usage only.
   */
  isFixed?: boolean;

  /**
   * Indicates if the cell is visible or not.
   * For internal usage only.
   */
  isVisible?: boolean;
}

/**
 * @private
 */
const SortArrow = ({
  direction,
}: {
  direction: ColumnDirection;
}): JSX.Element => (
  <Icon
    color={'text_light'}
    icon={direction === 'asc' ? 'arrow_up' : 'arrow_down'}
    css={css`
      margin-top: 3px;
      min-width: ${tokens.spacer.x2};
    `}
  />
);

/**
 * List of props to use in the styled component
 * @private
 */
const styledHeadCellPropNames = [
  'isSortable',
  'sortDirection',
  'onSort',
  'isFixed',
] as const;

/**
 * Styled component for the HeadCell
 * @private
 */
const StyledHeadCell = styled('th', {
  shouldForwardProp: shouldForwardProp(styledHeadCellPropNames),
})<HeadCellProps>`
  display: table-cell;
  vertical-align: top;
  ${tokens.text('subtitle2')}
  background-color: ${tokens.color.neutral_100};
  border-bottom: 1px solid ${tokens.color.ui_border};
  text-align: left;
  padding: ${({ isSortable }) =>
    isSortable ? 0 : `${TABLE_TH_TOP_BOTTOM_PADDING} ${tokens.spacer.x2}`};
  text-transform: none; // temp solution until the old styles from IWA are removed
  margin-bottom: 0;

  &:last-of-type {
    border-right: 1px solid ${tokens.color.neutral_100};
  }
`;

/**
 * The button used in the head cell to handle the sorting.
 * @private
 */
const SortButton = styled.button<
  Pick<HeadCellProps, 'sortDirection' | 'isFixed' | 'isVisible'>
>`
  ${tokens.text('subtitle2')}
  position: relative;
  display: flex;
  justify-content: space-between;
  border: none;
  background-color: ${({ sortDirection }) =>
    sortDirection !== 'none'
      ? tokens.color.neutral_200
      : tokens.color.neutral_100};
  color: ${tokens.color.text_default};
  margin: 0;
  text-align: left;
  cursor: pointer;
  outline: none;
  width: 100%;
  height: inherit;
  padding: ${TABLE_TH_TOP_BOTTOM_PADDING} ${tokens.spacer.x2};
  border-bottom: ${({ isFixed }) =>
    isFixed && `1px solid ${tokens.color.ui_border}`};

  & > svg {
    visibility: ${({ sortDirection, isVisible }) =>
      isVisible ? (sortDirection === 'none' ? 'hidden' : 'visible') : 'hidden'};
  }

  &:hover {
    background-color: ${tokens.color.neutral_200};

    & > svg {
      visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
    }
  }

  &:focus {
    // we're making an inset shadow here, otherwise
    // we can see the focus ring over the fixed right column
    box-shadow: inset 0 0 0 1px ${color.ui_background},
      inset 0 0 0 3px ${color.focus};
    border-color: transparent;
    border-bottom: ${({ isFixed }) =>
      isFixed && `1px solid ${tokens.color.ui_border}`};
  }
`;

const ContentWrapper = styled.span<Pick<HeadCellProps, 'isSortable'>>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-right: ${({ isSortable }) => (isSortable ? tokens.spacer.x2 : 0)};
`;

/**
 * The Table.HeadCell component is used to display a column header.
 * It can only display text or become a sortable cell.
 *
 * @example
 * <Table.HeadCell isSortable onSort={} sortDirection="desc">Sortable</Table.HeadCell>
 * <Table.HeadCell>Text content</Table.HeadCell>
 */
export const HeadCell = ({
  isSortable = false,
  children,
  sortDirection = 'none',
  sortLabel,
  onSort,
  isFixed = true,
  isVisible = true,
  ...props
}: HeadCellProps): JSX.Element => {
  const onSortClick = () => {
    if (sortDirection === 'none') {
      onSort && onSort('desc');
    } else {
      onSort && onSort(sortDirection === 'asc' ? 'desc' : 'asc');
    }
  };

  return (
    <StyledHeadCell {...props} isSortable={isSortable} isFixed={isFixed}>
      {isSortable ? (
        <SortButton
          onClick={onSortClick}
          sortDirection={sortDirection}
          isFixed={isFixed}
          isVisible={isVisible}
          aria-label={sortLabel}
        >
          <ContentWrapper isSortable>{children}</ContentWrapper>
          <SortArrow direction={sortDirection} />
        </SortButton>
      ) : (
        <ContentWrapper>{children}</ContentWrapper>
      )}
    </StyledHeadCell>
  );
};
